import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { getKongLocationAll } from 'features/Location/services/location'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import { GetKongLocationAllResponseType } from 'features/Location/@types/location'
import {
  getConversionList,
  GetConversionListRequestType,
} from 'features/Product/services/conversionRule'
import {
  getConversionTask,
  cancelConversionTask,
  getConversionTaskDetail,
  postAssignPicConversionTask,
  postCompleteConversionTask,
  getPicList,
  postCreateConversionTask,
  CreateConversionTaskPayloadType,
  GetConversionTaskParamsType,
} from 'features/Product/services/conversionTask'
import { toastSuccess } from 'utils/toast'

export const SLICE_NAME = 'conversionTask'

const getParamsDataForSubmit = (
  queryParams: GetConversionTaskParamsType,
  selectedLocation: GetKongLocationAllResponseType['location'][number] | null,
) => {
  const { order_number, status, page_index, page_size } = queryParams

  return {
    ...(order_number ? { order_number } : {}),
    ...(selectedLocation ? { location_id: selectedLocation.location_id } : {}),
    ...(status !== 'ALL' ? { status } : {}),
    page_index: page_index ?? 1,
    page_size: page_size ?? 20,
  } as Parameters<typeof getConversionTask>[0]
}

export const fetchConversionTaskList = createAsyncThunk(
  `${SLICE_NAME}/fetchConversionTaskList`,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async (_: undefined, { getState, rejectWithValue }) => {
    try {
      const { queryParams, selectedLocation } = (getState() as StoreStateType).conversionTask
      const paramsData = getParamsDataForSubmit(queryParams, selectedLocation)
      const { data } = await getConversionTask(paramsData)
      return data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetLocations = createAsyncThunk(
  `${SLICE_NAME}/fetchGetLocations`,
  async (name: undefined | string, { rejectWithValue }) => {
    try {
      const { data } = await getKongLocationAll({
        params: {
          name: name || '',
          active: true,
          page: 0,
          limit: 20,
        },
      })

      return data.data.location
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const cancelConversionTaskItem = createAsyncThunk(
  `${SLICE_NAME}/cancelConversionTaskItem`,
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await cancelConversionTask(id)
      toastSuccess('Conversion task berhasil dibatalkan')
      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchConversionTaskDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchConversionTaskDetail`,
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await getConversionTaskDetail(id)
      return data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const getPicConversionTask = createAsyncThunk(
  `${SLICE_NAME}/getPicConversionTask`,
  async (_, { getState, rejectWithValue }) => {
    const { conversionTaskDetail } = (getState() as StoreStateType).conversionTask
    try {
      const results = await Promise.all(
        [
          'AUTH_AC_KITCHEN_CREW',
          'AUTH_AC_KITCHEN_STORE_LEADER',
          'AUTH_QA_HUB_FRESH',
          'AUTH_QA_FRESH_HUB',
          'AUTH_QA_SHIFT_LEAD',
          'AUTH_KITCHEN_CREW',
          'AUTH_KITCHEN_STAFF',
          'AUTH_KITCHEN_SHIFT_LEAD',
          'AUTH_AC_KITCHEN_SHIFT_LEAD',
          'AUTH_INBOUND_ALL_TEAM_LEADER',
          'AUTH_INBOUND_LABELING_STAFF',
          'AUTH_INBOUND_ADMIN',
          'AUTH_INVENTORY_PUTAWAY_TEAM_LEADER',
          'AUTH_INVENTORY_REPLENISHMENT_TEAM_LEADER',
          'AUTH_INVENTORY_TROUBLESHOOTER_TEAM_LEADER',
        ].map((roleName) => getPicList(Number(conversionTaskDetail?.location.id), roleName)),
      )
      return results.flatMap(({ data }) => data.data)
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const assignReassignConversionTask = createAsyncThunk(
  `${SLICE_NAME}/assignReassignConversionTaskType`,
  async (params: { id: number; callbackFunction: () => void }, { getState, rejectWithValue }) => {
    const { modalAssignPic } = (getState() as StoreStateType).conversionTask
    try {
      const { id, callbackFunction } = params
      const { data } = await postAssignPicConversionTask(id, {
        staff_id: modalAssignPic.staffId,
      })
      toastSuccess('Conversion task berhasil diassign')
      if (callbackFunction) callbackFunction()
      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const completeConversionTask = createAsyncThunk(
  `${SLICE_NAME}/completeConversionTask`,
  async (params: { id: number; callbackFunction?: () => void }, { rejectWithValue }) => {
    try {
      const { id, callbackFunction } = params
      const { data } = await postCompleteConversionTask(id)
      toastSuccess('Conversion task berhasil diselesaikan')
      if (callbackFunction) callbackFunction()
      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchManufacturingRuleList = createAsyncThunk(
  `${SLICE_NAME}/fetchManufacturingRuleList`,
  async (
    arg: {
      params: GetConversionListRequestType
      type: 'manufacturingRuleList' | 'resultRulesByProductDestination'
    },
    { rejectWithValue },
  ) => {
    try {
      const { params } = arg
      const {
        data: { data },
      } = await getConversionList(params)
      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const createConversionTask = createAsyncThunk(
  `${SLICE_NAME}/createConversionTask`,
  async (
    param: { payload: CreateConversionTaskPayloadType; callbackFunction: () => void },
    { rejectWithValue },
  ) => {
    try {
      const { payload, callbackFunction } = param
      const { data } = await postCreateConversionTask(payload)
      toastSuccess('Conversion task berhasil dibuat')
      if (callbackFunction) callbackFunction()
      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)
